import * as React from "react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import { FaCheck } from "react-icons/fa"
import Image from "@p-ban/common/src/components/html/Image"
import { cn } from "../../../../../../utils/cn"
import type { EventHandlers, CheckboxChangeEvent } from "../../../../types"

type Props = {
  name: string
  checked: boolean
  onCheckedChange?: EventHandlers["checkbox"]
  src?: string
  className?: string
  disabled?: boolean
  children?: React.ReactNode
}

const ServiceCheckBox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, Props>(
  ({ children, src, name, checked, onCheckedChange, className, disabled = false }, ref) => {
    const handleChange = (isChecked: boolean) => {
      if (!onCheckedChange) return

      const event: CheckboxChangeEvent = {
        target: {
          name,
          checked: isChecked,
          type: "checkbox",
        },
      }
      onCheckedChange(event)
    }

    return (
      <label
        className={cn(
          "flex h-20 cursor-pointer items-center justify-between rounded-lg bg-white p-2 transition-all",
          "border border-[#cdd7e1]",
          checked && "border border-[#51bc51] shadow-[0_0_0_2px_#51bc51]",
          disabled && "cursor-not-allowed opacity-50",
          className,
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-0">
          <CheckboxPrimitive.Root
            ref={ref}
            name={name}
            checked={checked}
            onCheckedChange={handleChange}
            disabled={disabled}
            className={cn(
              "h-6 w-6 shrink-0 rounded border-2",
              "border-gray-300 data-[state=checked]:border-[#51bc51] data-[state=checked]:bg-[#51bc51]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#51bc51] focus-visible:ring-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            <CheckboxPrimitive.Indicator className="flex items-center justify-center text-white">
              <FaCheck className="h-3 w-3" />
            </CheckboxPrimitive.Indicator>
          </CheckboxPrimitive.Root>
          <span className={cn("ml-2 min-w-0 text-base font-bold", checked ? "text-[#32383e]" : "text-[#9fa6ad]")}>
            {children}
          </span>
        </div>
        {src && (
          <div className="flex h-16 w-16 items-center justify-center">
            <Image src={src} alt="" width={64} height={64} className="object-contain" />
          </div>
        )}
      </label>
    )
  },
)

ServiceCheckBox.displayName = "ServiceCheckBox"

export default ServiceCheckBox
